import type { StateStorage } from 'zustand/middleware'
import {
  WorkspaceRevisionConflictError,
  type WorkspaceDirectory,
  type WorkspaceRepository,
} from './WorkspaceRepository'
import { LegacyLocalStorageAdapter } from './LegacyLocalStorageAdapter'
import { storageHealth } from '@/services/storage-health'
import type { WorkspaceCommitCoordinator } from './WorkspaceCommitCoordinator'

function hash(value: string): string {
  let result = 2166136261
  for (let index = 0; index < value.length; index += 1) {
    result ^= value.charCodeAt(index)
    result = Math.imul(result, 16777619)
  }
  return (result >>> 0).toString(16)
}

function createStorageInstanceId(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID()
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

function extractWorkspaceState(value: string): { studentIndex: WorkspaceDirectory['studentIndex']; workspaces: WorkspaceDirectory['workspaces'] } {
  const parsed = JSON.parse(value) as { state?: Partial<WorkspaceDirectory> }
  const state = parsed.state
  if (!state || !Array.isArray(state.studentIndex) || !state.workspaces || typeof state.workspaces !== 'object') {
    throw new Error('Persisted Zustand state does not contain a valid student Workspace directory')
  }
  return { studentIndex: state.studentIndex, workspaces: state.workspaces }
}

/**
 * Compatibility Facade for Zustand persist. The repository is the only writer;
 * this class never falls back to a legacy browser key-value store.
 */
export class WorkspaceStateStorage implements StateStorage {
  private revision: number | null = null
  private lastCommittedValue: string | null = null
  private lastQueuedValue: string | null = null
  private lastQueuedWrite: Promise<void> | null = null
  private sequence = 0
  private readonly instanceId = createStorageInstanceId()
  private migrationPromise: Promise<void> | null = null

  constructor(
    private readonly repository: WorkspaceRepository,
    private readonly legacyRepository?: LegacyLocalStorageAdapter,
    private readonly coordinator?: WorkspaceCommitCoordinator,
  ) {}

  async getItem(_name: string): Promise<string | null> {
    try {
      let snapshot = await this.repository.load()
      if (snapshot.revision === 0 && !snapshot.serializedState && this.legacyRepository) {
        this.migrationPromise ??= this.migrateLegacy()
        await this.migrationPromise
        snapshot = await this.repository.load()
      }
      this.revision = snapshot.revision
      storageHealth.clear()
      return snapshot.serializedState ?? null
    } catch (error) {
      storageHealth.reportFailure(`学习数据恢复失败，系统没有覆盖旧数据：${error instanceof Error ? error.message : String(error)}`)
      throw error
    }
  }

  setItem(name: string, value: string): Promise<void> {
    if (this.coordinator?.consumeDiscardedWrite()) return Promise.resolve()
    if (this.coordinator) {
      if (value === this.lastQueuedValue && this.lastQueuedWrite) return this.lastQueuedWrite
      if (!this.lastQueuedWrite && value === this.lastCommittedValue) return Promise.resolve()
      const write = this.coordinator.enqueue(() => this.setItemInternal(name, value))
      this.lastQueuedValue = value
      this.lastQueuedWrite = write
      void write.then(
        () => this.clearQueuedWrite(write),
        () => this.clearQueuedWrite(write),
      )
      return write
    }
    return this.setItemInternal(name, value)
  }

  private clearQueuedWrite(write: Promise<void>): void {
    if (this.lastQueuedWrite !== write) return
    this.lastQueuedValue = null
    this.lastQueuedWrite = null
  }

  private async setItemInternal(name: string, value: string): Promise<void> {
    if (value === this.lastCommittedValue) return
    const current = this.revision === null ? await this.repository.load() : null
    const expectedRevision = current?.revision ?? this.revision!
    const workspaceState = extractWorkspaceState(value)
    const mutationId = `zustand:${name}:${this.instanceId}:${++this.sequence}:${hash(value)}`
    const result = await this.repository.transact(expectedRevision, mutationId, (draft) => {
      draft.studentIndex = workspaceState.studentIndex
      draft.workspaces = workspaceState.workspaces
      draft.serializedState = value
    })
    if (result.status === 'conflict') {
      storageHealth.reportConflict()
      throw new WorkspaceRevisionConflictError(result)
    }
    if (result.status === 'readonly') throw new Error('Workspace storage is read-only during legacy migration')
    this.revision = result.revision
    this.lastCommittedValue = value
    storageHealth.clear()
  }

  async removeItem(_name: string): Promise<void> {
    throw new Error('Workspace state removal is disabled; use an explicit recovery or reset command')
  }

  subscribe(listener: (snapshot: WorkspaceDirectory) => void): () => void {
    return this.repository.subscribe((snapshot) => {
      this.revision = snapshot.revision
      listener(snapshot)
    })
  }

  private async migrateLegacy(): Promise<void> {
    const legacy = await this.legacyRepository?.load()
    if (!legacy || legacy.studentIndex.length === 0) return
    const result = await this.repository.transact(0, 'migration:legacy-local-storage:v3', (draft) => {
      draft.studentIndex = legacy.studentIndex
      draft.workspaces = legacy.workspaces
      draft.serializedState = legacy.serializedState
    })
    if (result.status === 'conflict' || result.status === 'readonly') {
      throw new WorkspaceRevisionConflictError({
        status: 'conflict', mutationId: 'migration:legacy-local-storage:v3', expectedRevision: 0,
        revision: result.revision, snapshot: result.snapshot,
      })
    }
  }
}
